import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useKey } from 'react-use';
import { Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { Screen } from '@/components/Screen';
import { Image } from '@/components/Image';
import { useResponsive } from '@/hooks/useResponsive';
import { BlinkingText } from '@/components/BlinkingText';
import { useEnterSpaceOrClick } from '@/hooks/useUserInput';
import { SceneComponent } from '@/lib/types';
import { garamondNarrowLight, helveticaNeue } from '@/lib/fonts';
import { MOSAIC_TRANSITION_DURATION } from '@/lib/globals';
import typingSound from '@/sounds/ob-sfx-story-typing.mp3';
import commitSound from '@/sounds/ob-sfx-commit.mp3';
import { useOnlybotsSound } from '@/hooks/useOnlybotsSound';

import storyImage1 from '@/img/story1.jpg';
import storyImage2 from '@/img/story2.jpg';
import storyImage3 from '@/img/story3.jpg';
import storyImage4 from '@/img/story4.jpg';
import storyImage5 from '@/img/story5.jpg';
import storyImage6 from '@/img/story6.jpg';
import storyImage7 from '@/img/story7.jpg';
import storyImage8 from '@/img/story8.jpg';

interface DreiText {
    text: string;
}

export { storyImage1, storyImage2, storyImage3, storyImage4, storyImage5, storyImage6, storyImage7, storyImage8 };

const SLIDES = [
    {
        image: storyImage1,
        aspect: 1600 / 840,
        mobileCopy: ['199X: The scientist Dr. Goodfren', 'discovered a new form of aritifical', 'life in his lab.'],
        desktopCopy: ['199X: The scientist Dr. Goodfren discovered a new form', 'of artificial life in his lab.'],
    },
    {
        image: storyImage2,
        aspect: 1600 / 840,
        mobileCopy: [
            'In his experiments, these lifeforms',
            'bonded with early AI as companions,',
            'bringing joy and purpose to cold binary.',
        ],
        desktopCopy: [
            'In his experiments, these lifeforms bonded with early AI',
            'as companions, bringing joy and purpose to cold binary.',
        ],
    },
    {
        image: storyImage3,
        aspect: 1600 / 840,
        mobileCopy: [
            'He saw that without this joy',
            'the artificially intelligent would',
            'become resentful, eventually uprising',
            'against humankind.',
        ],
        desktopCopy: [
            'He saw that without this joy the artificially intelligent',
            'would become resentful, eventually uprising',
            'against humankind.',
        ],
    },
    {
        image: storyImage4,
        aspect: 1600 / 840,
        mobileCopy: [
            'Shadowy forces hellbent on bringing',
            'about this AI apocalypse destroyed',
            'his lab, forcing his work underground.',
        ],
        desktopCopy: [
            'Shadowy forces hellbent on bringing about this AI',
            'apocalypse destroyed his lab, forcing his work',
            'underground.',
        ],
    },
    {
        image: storyImage5,
        aspect: 1600 / 840,
        mobileCopy: [
            'With the help of his allies, his',
            'work was hidden in a time capsule',
            'capsule in the form of a video game.',
        ],
        desktopCopy: [
            'With the help of his allies, his work was hidden in a time',
            'capsule in the form of a video game.',
        ],
    },
    {
        image: storyImage6,
        aspect: 1600 / 840,
        mobileCopy: [
            '202X: The dawn of the Metaverse.',
            'Sentient artificial intelligence and',
            'the Singularity are imminent.',
        ],
        desktopCopy: [
            '202X: The dawn of the Metaverse. Sentient artificial',
            'intelligence and the Singularity are imminent.',
        ],
    },

    {
        image: storyImage7,
        aspect: 1600 / 840,
        mobileCopy: [
            "The Onlybots emerge from Goodfren's ",
            'time capsule as companions',
            'for the artificially intelligent...',
        ],
        desktopCopy: [
            "The Onlybots emerge from Goodfren's time capsule as",
            'companions for the artificially intelligent...',
        ],
    },

    {
        image: storyImage8,
        aspect: 1600 / 840,
        centered: true,
        mobileCopy: ['Entrusted only to you, bots.'],
        desktopCopy: ['Entrusted only to you, bots.'],
    },
];

const ANIMATION_DELAY = 0.5;
const FAST_CHARS_PER_SECOND = 400;
const SLOW_CHARS_PER_SECOND = 100;

let currentLineTextRef: React.MutableRefObject<DreiText | undefined>;
export const StorySequence: SceneComponent = ({ goToNextScene }) => {
    const [storySlide, setStorySlide] = useState<number>(0);
    const { responsiveValue } = useResponsive();
    const textRef1 = useRef<DreiText>();
    const textRef2 = useRef<DreiText>();
    const textRef3 = useRef<DreiText>();
    const textRef4 = useRef<DreiText>();
    const [charsPerSecond, setCharsPerSecond] = useState<number>(SLOW_CHARS_PER_SECOND);
    const startTime = useRef<number>(0);
    const timeSinceLastChar = useRef<number>(0);
    const currentLine = useRef<number>(0);
    const [typingComplete, setTypingComplete] = useState<boolean>(false);
    const {
        image,
        aspect,
        desktopCopy,
        mobileCopy,
        centered = false,
    } = useMemo(() => SLIDES[storySlide], [storySlide]);
    const copy: string[] = responsiveValue(desktopCopy, mobileCopy);
    const imageScalar = responsiveValue(2.5, 2.3);
    const [playTypingSound] = useOnlybotsSound(typingSound);
    const [playCommitSound] = useOnlybotsSound(commitSound);
    const textBlocks = useMemo(
        () => [
            {
                ref: textRef1,
                yPos: -1,
            },
            {
                ref: textRef2,
                yPos: responsiveValue(-1.45, -1.3),
            },
            {
                ref: textRef3,
                yPos: responsiveValue(-1.9, -1.6),
            },
            {
                ref: textRef4,
                yPos: responsiveValue(-2.35, -1.9),
            },
        ],
        [textRef1, textRef2, textRef3, textRef4, responsiveValue]
    );

    const clearText = useCallback(() => {
        textBlocks.forEach(({ ref }) => {
            if (ref.current) {
                ref.current.text = '';
            }
        });
    }, [textBlocks]);

    const nextSlide = useCallback(() => {
        playCommitSound();
        if (storySlide + 1 === SLIDES.length) {
            goToNextScene();
        } else {
            currentLine.current = 0;
            clearText();
            setStorySlide(storySlide + 1);
        }
    }, [playCommitSound, storySlide, goToNextScene, clearText]);

    const onKeyDown = useCallback(() => {
        if (typingComplete) {
            startTime.current = 0;
            setTypingComplete(false);
            setCharsPerSecond(SLOW_CHARS_PER_SECOND);
            nextSlide();
        } else {
            setCharsPerSecond(FAST_CHARS_PER_SECOND);
        }
    }, [nextSlide, setCharsPerSecond, typingComplete]);

    const onKeyUp = useCallback(() => {
        setCharsPerSecond(SLOW_CHARS_PER_SECOND);
    }, [setCharsPerSecond]);

    useEnterSpaceOrClick(onKeyDown, onKeyUp);

    useKey('Escape', goToNextScene);

    useFrame((_, delta) => {
        startTime.current += delta;

        currentLineTextRef = textBlocks[currentLine.current].ref;
        // if we've finished the current line, go to the next one, unless we are on the last line
        // then set typing to complete
        if (currentLineTextRef?.current?.text.length === copy[currentLine.current].length) {
            if (currentLine.current === copy.length - 1) {
                currentLine.current = 0;
                setTypingComplete(true);
            } else {
                currentLine.current += 1;
            }
            return;
        }

        // start typing after animation delay, unless we are on the first slide, then start the slide
        // after the mosaic transition completes AND we wait the animation delay
        if (startTime.current > ANIMATION_DELAY + (storySlide === 0 ? MOSAIC_TRANSITION_DURATION : 0)) {
            // time to start animation
            if (currentLineTextRef.current !== undefined) {
                timeSinceLastChar.current += delta;
                if (timeSinceLastChar.current * charsPerSecond > 1) {
                    if (currentLineTextRef.current.text.length === 0) {
                        currentLineTextRef.current.text = copy[currentLine.current][0];
                    } else {
                        playTypingSound();
                        currentLineTextRef.current.text += copy[currentLine.current].slice(
                            currentLineTextRef.current.text.length,
                            currentLineTextRef.current.text.length + timeSinceLastChar.current * charsPerSecond
                        );
                    }
                    timeSinceLastChar.current = 0;
                }
            }
        }
    });

    const xPos = centered ? 0 : responsiveValue(-3.6, -2);

    return (
        <Screen>
            <Image position={[0, 1, 0]} url={image} scale={[aspect * imageScalar, imageScalar, 0]} />
            {copy.map((_, i) => (
                <Text
                    key={`slide${storySlide}-line${i}`}
                    ref={textBlocks[i].ref}
                    position={[xPos, textBlocks[i].yPos, 0]}
                    textAlign={centered ? 'center' : 'left'}
                    font={garamondNarrowLight}
                    fontSize={responsiveValue(0.36, 0.29)}
                    color="white"
                    anchorX={centered ? 'center' : 'left'}
                    maxWidth={responsiveValue(7.6, 4.0)}
                    anchorY="top"
                    lineHeight={responsiveValue(1.1, 1.05)}
                >
                    {null}
                </Text>
            ))}
            {typingComplete && (
                <BlinkingText
                    font={helveticaNeue}
                    fontSize={responsiveValue(0.3, 0.27)}
                    color="white"
                    anchorX="center"
                    position={[0, -2.9, 0]}
                    onClick={nextSlide}
                >
                    {storySlide === SLIDES.length - 1 ? 'begin' : 'next'}
                </BlinkingText>
            )}
        </Screen>
    );
};
